import { ref, computed, unref, onUnmounted, getCurrentInstance } from 'vue';
import dayjs from '@/js/day.js'; // preconfigured: locale + relativeTime/calendar/localizedFormat plugins
import i18n from '@/js/i18n.js';

const t = i18n.global.t;

/**
 * Central date/time formatting — single source of truth.
 *
 * Built on dayjs (already configured in js/day.js with the app locale,
 * relativeTime, calendar and localizedFormat plugins).
 *
 * Layers:
 *  - parseDate / formatDate / formatDateTime / formatTime : absolute formats
 *  - timeAgo(style)          : "5 minutes ago" (long) or "5m" (compact)
 *  - smartTimestamp          : messenger-style list stamp (Slack/WhatsApp rules)
 *  - msgDate                 : legacy chat filter kept for compatibility
 *  - useTimeAgo/useSmartTimestamp : live-updating reactive variants that
 *    share ONE ticker while subscribed (industry practice for chat lists
 *    and feeds so timestamps age without re-renders storms).
 */

/** Tolerant parse: '' | null | ISO string | epoch millis | Date -> dayjs|null */
export function parseDate(value) {
  if (value === '' || value === null || value === undefined) return null;
  let d;
  if (value instanceof Date) d = value;
  else if (typeof value === 'number') d = new Date(value);
  else if (typeof value === 'string') {
    const parsed = Date.parse(value);
    d = Number.isNaN(parsed) ? new Date(value) : new Date(parsed);
  } else {
    const asNumber = Number.parseInt(value, 10);
    d = Number.isNaN(asNumber) ? new Date(value) : new Date(asNumber);
  }
  const dayjsDate = dayjs(d);
  return dayjsDate.isValid() ? dayjsDate : null;
}

export function formatDate(value, pattern = 'DD/MM/YYYY') {
  const d = parseDate(value);
  return d ? d.format(pattern) : '';
}

export function formatDateTime(value, pattern = 'lll') {
  return formatDate(value, pattern);
}

export function formatTime(value, pattern = 'HH:mm') {
  return formatDate(value, pattern);
}

// ── Relative time ────────────────────────────────────────────────────

/**
 * Humanized age of a timestamp.
 *
 * @param {*} value timestamp in any accepted shape
 * @param {'long'|'compact'} style
 *   long    -> dayjs locale text ("il y a 5 minutes", "in 3 hours")
 *   compact -> chat-badge style ("now", "5 min", "3 h", "2 j", "4 sem.", then date)
 */
export function timeAgo(value, style = 'long') {
  const d = parseDate(value);
  if (!d) return '';

  if (style === 'compact') {
    const diffSeconds = dayjs().diff(d, 'second');
    if (diffSeconds < 60) return t('common.justNow');
    const minutes = dayjs().diff(d, 'minute');
    if (minutes < 60) return t('common.agoMinutesShort', { n: minutes });
    const hours = dayjs().diff(d, 'hour');
    if (hours < 24) return t('common.agoHoursShort', { n: hours });
    const days = dayjs().diff(d, 'day');
    if (days < 7) return t('common.agoDaysShort', { n: days });
    const weeks = dayjs().diff(d, 'week');
    if (weeks < 5) return t('common.agoWeeksShort', { n: weeks });
    return d.format('DD/MM/YYYY');
  }

  return d.fromNow();
}

/**
 * Messenger-list timestamp (WhatsApp/Discord/Slack convention):
 *   < 1 min      -> "Just now"
 *   today        -> "14:32"
 *   yesterday    -> "Yesterday 14:32"
 *   last 7 days  -> weekday name ("Wednesday")
 *   same year    -> "3 Mar"
 *   older        -> "03/03/2025"
 */
export function smartTimestamp(value) {
  const d = parseDate(value);
  if (!d) return '';

  const now = dayjs();
  if (now.diff(d, 'second') < 60) return t('common.justNow');

  if (d.isSame(now, 'day')) return d.format('HH:mm');

  if (d.isSame(now.subtract(1, 'day'), 'day')) {
    return `${t('common.yesterday')} ${d.format('HH:mm')}`;
  }

  if (d.isAfter(now.subtract(7, 'day'))) return d.format('dddd');

  if (d.isSame(now, 'year')) return d.format('D MMM');

  return d.format('DD/MM/YYYY');
}

// ── Legacy adapters (previous $filtersToDate behavior, byte-compatible) ──

/** Old $filtersToDate.toDateTime — returns undefined for '' like before. */
export function toDateTime(value) {
  if (value === '') return undefined;
  const d = parseDate(value);
  return d ? d.format('DD/MM/YYYY') : undefined;
}

/** Old $filtersToDate.msgDate — chat group headers ('time' | anything else). */
export function msgDate(value, type) {
  if (value === '') return undefined;
  const d = parseDate(value);
  if (!d) return undefined;

  const now = dayjs();
  if (d.isSame(now, 'day')) {
    return type === 'time' ? d.format('HH:mm') : t('common.today');
  }
  if (d.isSame(now.subtract(1, 'day'), 'day')) return t('common.yesterday');
  if (d.isSame(now, 'week')) return d.format('dddd');
  return d.format('DD/MM/YYYY');
}

// ── Live (auto-aging) variants ───────────────────────────────────────

const TICK_MS = 30_000;
let tickerSubscribers = 0;
let tickerTimer = null;
const tickerListeners = new Set();

function acquireTicker() {
  tickerSubscribers += 1;
  if (tickerTimer) return;
  tickerTimer = setInterval(() => {
    // Skip background churn — timestamps refresh when the tab is visible again.
    if (typeof document !== 'undefined' && document.hidden) return;
    tickerListeners.forEach((fn) => fn());
  }, TICK_MS);
}

function releaseTicker() {
  tickerSubscribers -= 1;
  if (tickerSubscribers > 0 || !tickerTimer) return;
  clearInterval(tickerTimer);
  tickerTimer = null;
}

function useLiveTicker() {
  const tick = ref(0);
  const listener = () => {
    tick.value += 1;
  };
  tickerListeners.add(listener);
  acquireTicker();

  if (getCurrentInstance()) {
    onUnmounted(() => {
      tickerListeners.delete(listener);
      releaseTicker();
    });
  }
  return tick;
}

function makeLive(fn, source, options) {
  const tick = useLiveTicker();
  return computed(() => {
    void tick.value; // re-evaluates every shared tick
    return fn(unref(source), options);
  });
}

/**
 * Reactive timeAgo — e.g. `useTimeAgo(() => comment.sentAt)` renders
 * "just now" then ages automatically every 30s while mounted.
 */
export function useTimeAgo(source, style = 'long') {
  return makeLive(timeAgo, source, style);
}

/** Reactive smartTimestamp for chat/message list rows. */
export function useSmartTimestamp(source) {
  return makeLive(smartTimestamp, source, undefined);
}
